//@ts-nocheck
import React, { createContext } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import {
  createGenerateClassName,
  createTheme,
  alpha,
} from "@material-ui/core/styles";
import {
  StylesProvider,
  ThemeProvider as MuiThemeProvider,
} from "@material-ui/styles";

const generateClassName = createGenerateClassName();

const FONT = "Roboto, sans-serif";

// Type scale
const typeScale = {
  h1: { fontSize: "32px", fontWeight: 800, lineHeight: 1.25 },
  h2: { fontSize: "24px", fontWeight: 700, lineHeight: 1.3 },
  h3: { fontSize: "20px", fontWeight: 600, lineHeight: 1.35 },
  h4: { fontSize: "18px", fontWeight: 600, lineHeight: 1.4 },
  h5: { fontSize: "16px", fontWeight: 600, lineHeight: 1.4 },
  h6: { fontSize: "14px", fontWeight: 600, lineHeight: 1.5 },
  subtitle1: { fontSize: "16px", fontWeight: 500, lineHeight: 1.5 },
  subtitle2: { fontSize: "14px", fontWeight: 500, lineHeight: 1.5 },
  body1: { fontSize: "16px", fontWeight: 400, lineHeight: 1.6 },
  body2: { fontSize: "14px", fontWeight: 400, lineHeight: 1.6 },
  caption: { fontSize: "12px", fontWeight: 400, lineHeight: 1.5 },
  overline: { fontSize: "10px", fontWeight: 500, lineHeight: 1.5, letterSpacing: "0.08em", textTransform: "uppercase" as const },
  button: { fontSize: "14px", fontWeight: 600, lineHeight: 1.5, textTransform: "none" as const },
};

const ThemeContext = createContext({});

const ThemeProvider = ({ children }: any) => {
  const fontSizes = [18, 14, 12];

  let theme = createTheme({
    direction: "ltr",
    palette: {
      primary: {
        light: "#1E70FC",
        main: "#0040AC",
        dark: "#903d27",
        contrastText: "#fff",
      },
      secondary: {
        light: "#56565699",
        main: "#565656",
        dark: "#565656",
        contrastText: "#fff",
      },
      border: {
        grayD3: "#D3D6D8",
        greyA7: "#A7ADB0",
      },
      error: {
        light: "#e57373",
        main: "#f44336",
        dark: "#d32f2f",
        contrastText: "#fff",
      },
      success: {
        light: "#81c784",
        main: "#4caf50",
        dark: "#388e3c",
        contrastText: alpha("#000", 0.87),
      },
      info: {
        light: "#64b5f6",
        main: "#2196f3",
        dark: "#1E70FC",
        contrastText: "#fff",
      },
      warning: {
        light: "#ffb74d",
        main: "#ff9800",
        dark: "#f57c00",
        contrastText: alpha("#000", 0.87),
      },
      text: {
        primary: "#000000",
        secondary: "#49545B",
        third: "#E8E9EB",
        gray7c: "#6D767C",
        h4: "#49545B",
        hint: "#A7ADB0",
      },
      colors: {
        offWhite: "#F1F1F1",
        body: "#F2F2F2",
        footer: "#6d6d6d",
        border: "#74727f",
        border2: "#E7E7E7",
        header: "#FFFFFF",
        backgroundLight: "#F4F8FB",
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
    typography: {
      fontFamily: FONT,
      htmlFontSize: 16,
      fontSize: 14,
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,
      ...typeScale,
    },
    overrides: {
      MuiButton: {
        root: {
          fontFamily: FONT,
          fontSize: typeScale.button.fontSize,
          fontWeight: typeScale.button.fontWeight,
          lineHeight: typeScale.button.lineHeight,
          textTransform: typeScale.button.textTransform,
          borderRadius: "8px",
          boxShadow: "none",
          padding: "10px 20px",
          "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.12)" },
          "&:active": { boxShadow: "none" },
          "&:focus": { boxShadow: "none" },
        },
        contained: {
          fontSize: typeScale.button.fontSize,
          fontWeight: typeScale.button.fontWeight,
          boxShadow: "none",
          "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.12)" },
          "&:active": { boxShadow: "none" },
        },
        sizeSmall: {
          padding: "8px 16px",
          fontSize: "13px",
        },
        outlined: {
          fontSize: typeScale.button.fontSize,
          fontWeight: typeScale.button.fontWeight,
        },
        text: {
          fontSize: typeScale.button.fontSize,
          fontWeight: typeScale.button.fontWeight,
        },
      },
      MuiInputBase: {
        root: {
          fontFamily: FONT,
          fontSize: typeScale.body2.fontSize,
          fontWeight: typeScale.body2.fontWeight,
          lineHeight: '1.4',
        },
      },
      MuiOutlinedInput: {
        root: {
          borderRadius: '8px',
          fontSize: '14px',
          height: '48px',
        },
        input: {
          height: '20px',
          padding: '13px 14px',
          fontSize: '14px',
          lineHeight: '20px',
          boxSizing: 'content-box',
        },
        multiline: {
          height: 'auto',
          padding: '13px 14px',
        },
        notchedOutline: {
          borderColor: '#D1D5DB',
        },
      },
      MuiSelect: {
        root: {
          fontSize: '14px',
          height: '48px',
          display: 'flex',
          alignItems: 'center',
          boxSizing: 'border-box',
          padding: '0 14px',
        },
        select: {
          height: '48px',
          display: 'flex',
          alignItems: 'center',
          padding: '0 14px',
          boxSizing: 'border-box',
          '&:focus': {
            background: 'transparent',
          },
        },
      },
      MuiInputLabel: {
        root: {
          fontFamily: FONT,
          fontSize: '14px',
          fontWeight: 400,
          color: '#9CA3AF',
          transform: 'translate(14px, 14px) scale(1)',
        },
        shrink: {
          transform: 'translate(14px, -6px) scale(0.85)',
          color: '#374151',
          fontWeight: 500,
        },
      },
      MuiFormHelperText: {
        root: {
          fontFamily: FONT,
          fontSize: typeScale.caption.fontSize,
          fontWeight: typeScale.caption.fontWeight,
          lineHeight: typeScale.caption.lineHeight,
        },
      },
      MuiMenuItem: {
        root: {
          fontFamily: FONT,
          fontSize: typeScale.body2.fontSize,
          fontWeight: typeScale.body2.fontWeight,
        },
      },
      MuiTableCell: {
        root: {
          fontFamily: FONT,
          fontSize: typeScale.body2.fontSize,
        },
        head: {
          fontWeight: 600,
          fontSize: typeScale.body2.fontSize,
        },
      },
      MuiTab: {
        root: {
          fontFamily: FONT,
          fontSize: typeScale.body2.fontSize,
          fontWeight: 500,
          textTransform: "none",
        },
      },
      MuiTooltip: {
        tooltip: {
          fontFamily: FONT,
          fontSize: typeScale.caption.fontSize,
        },
      },
      MuiChip: {
        root: {
          fontFamily: FONT,
          fontSize: typeScale.caption.fontSize,
          fontWeight: 500,
        },
      },
      MuiTypography: {
        root: {
          fontFamily: FONT,
        },
      },
      MuiCssBaseline: {
        '@global': {
          '@media (max-width: 600px)': {
            h1: { fontSize: '24px !important' },
            h2: { fontSize: '20px !important' },
            h3: { fontSize: '18px !important' },
          },
        },
      },
    },
  });

  return (
    <StylesProvider generateClassName={generateClassName}>
      <ThemeContext.Provider value={{ fontSizes }}>
        <MuiThemeProvider theme={theme}>
          <Router>{children}</Router>
        </MuiThemeProvider>
      </ThemeContext.Provider>
    </StylesProvider>
  );
};

const ThemeConsumer = ThemeContext.Consumer;

export { ThemeProvider, ThemeConsumer };
